import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useGetQueryParams } from '@/hooks/queryParams/useGetQueryParams';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { fetchCatalogsNearMeByBounds } from '@/redux/modules/auctionsNearMe/auctionsNearMe.actions';
import {
    getCatalogsNearMeSelector,
    getLoadingSelector,
    getMapPointsSelector,
    getPaginationInfoSelector,
    getTotalUniqueHousesSelector,
} from '@/redux/modules/auctionsNearMe/auctionsNearMe.selectors';
import { CatalogNearMe } from '@/redux/modules/auctionsNearMe/catalogsNearMe.types';
import { getLocale } from '@/redux/modules/intl';
import { getPageFromQueryParams } from '@/utils/queryParams';

import { MapBounds } from '../components/MapView/components/GoogleMap/AuctionsNearMeGoogleMap';
import {
    defaultAuctionsNearMeUkBounds,
    defaultAuctionsNearMeUsaBounds,
    defaultHitsPerPage,
    defaultPageNumber,
} from '../constants/AuctionsNearMeConstants';
import { createBoundingBoxString } from '../helpers/createBoundingBoxString';
import { AuctionsNearMeQueryParams } from '../types/AuctionsNearMe.types';
import { useGeoAreaFromUrl } from './useGeoAreaFromUrl';
import { useGeocodeLocation } from './useGeocodeLocation';

export const useAuctionsNearMeData = () => {
    const dispatch = useAppDispatch();
    const getQueryParams = useGetQueryParams<AuctionsNearMeQueryParams>();
    const queryParams = getQueryParams();

    // Extracted hooks
    const { geoArea, hasNewUrlParams, hasOldUrlParams, routeParams } = useGeoAreaFromUrl();

    // Redux selectors
    const catalogsNearMe = useAppSelector(getCatalogsNearMeSelector);
    const locale = useAppSelector(getLocale);
    const loading = useAppSelector(getLoadingSelector);
    const mapPoints = useAppSelector(getMapPointsSelector);
    const { totalRecords } = useAppSelector(getPaginationInfoSelector);
    const totalUniqueHouses = useAppSelector(getTotalUniqueHousesSelector);

    // Local state
    const [googleMapsReady, setGoogleMapsReady] = useState(false);
    const [customSearchBounds, setCustomSearchBounds] = useState<MapBounds | null>(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [geocoderFailed, setGeocoderFailed] = useState(false);

    // Filter state (sent to backend)
    const [topRatedFilter, setTopRatedFilter] = useState(false);
    const [localPickupFilter, setLocalPickupFilter] = useState(false);
    const [keywords, setKeywords] = useState<string[]>([]);

    // Pagination state — initialize from the URL's ?page= so SSR and the client agree on which
    // page's data was fetched. The sync effect below keeps it in step with the URL afterward
    // (browser back/forward, a bookmarked link); Pagination's own query-string push
    // (shouldModifyQueryParams in AuctionsNearMeCardsViewNew) is what changes ?page= on a click.
    const urlPageNumber = getPageFromQueryParams(queryParams);
    const [pageNumber, setPageNumber] = useState(urlPageNumber);
    const [pageSize, setPageSize] = useState(defaultHitsPerPage);

    // Refs — use refs for filter/pagination values so fetchCatalogs identity stays stable
    const lastFetchParamsRef = useRef<string | null>(null);
    const currentActiveBoundsRef = useRef<MapBounds | null>(null);
    const hasUserInteractedWithMapRef = useRef(false);
    const filtersRef = useRef({ keywords, localPickupFilter, pageNumber, pageSize, topRatedFilter });
    filtersRef.current = { keywords, localPickupFilter, pageNumber, pageSize, topRatedFilter };

    // Derived values
    const catalogIds = useMemo(() => catalogsNearMe.map((catalog: CatalogNearMe) => catalog.id), [catalogsNearMe]);
    const hasFilteredResults = catalogsNearMe.length > 0;

    const defaultBounds = useMemo(
        () => (locale === 'en-gb' ? defaultAuctionsNearMeUkBounds : defaultAuctionsNearMeUsaBounds),
        [locale]
    );

    // Stable fetch function — reads filter/pagination from refs
    const fetchCatalogs = useCallback(
        (bounds: MapBounds) => {
            const {
                keywords: kw,
                localPickupFilter: lp,
                pageNumber: pn,
                pageSize: ps,
                topRatedFilter: tr,
            } = filtersRef.current;
            const insideBoundingBox = createBoundingBoxString(bounds);
            const fetchKey = JSON.stringify({
                bounds: insideBoundingBox,
                hasFreeLocalPickup: lp || undefined,
                isTopRated: tr || undefined,
                page: pn,
                pageSize: ps,
                searchTerm: kw.join(',') || undefined,
            });

            if (lastFetchParamsRef.current === fetchKey) {
                return;
            }

            lastFetchParamsRef.current = fetchKey;

            dispatch(
                fetchCatalogsNearMeByBounds({
                    insideBoundingBox,
                    page: pn,
                    pageSize: ps,
                    ...(lp && { hasFreeLocalPickup: true }),
                    ...(tr && { isTopRated: true }),
                    ...(kw.length > 0 && { searchTerm: kw.join(',') }),
                })
            );
        },
        [dispatch]
    );

    // Get the currently active bounds
    const getActiveBounds = useCallback((): MapBounds => {
        if (geoArea?.northEastLatitude && geoArea?.southWestLatitude) {
            return {
                northEast: { lat: geoArea.northEastLatitude, lng: geoArea.northEastLongitude },
                southWest: { lat: geoArea.southWestLatitude, lng: geoArea.southWestLongitude },
            };
        }
        if (currentActiveBoundsRef.current) {
            return currentActiveBoundsRef.current;
        }
        if (customSearchBounds) {
            return customSearchBounds;
        }
        return defaultBounds;
    }, [customSearchBounds, geoArea, defaultBounds]);

    // Determine location to geocode. Only the ?search= custom flow needs this: the data loader
    // (auctionsNearMePageData.ts) already resolves every /:state/:city/ and /:state/ URL precisely
    // via the backend (fetchCatalogsNearMeByCityAndState), so geocoding a canonical URL would just
    // redundantly re-fetch and overwrite that SSR result with a bounds-based approximation.
    const locationToGeocode = useMemo(() => {
        if (queryParams.search && !customSearchBounds) {
            return queryParams.search;
        }

        return null;
    }, [queryParams.search, customSearchBounds]);

    // Geocode location
    const {
        bounds: geocodedBounds,
        failed: geocodingFailed,
        isGeocoding,
    } = useGeocodeLocation({
        googleMapsReady,
        location: locationToGeocode,
    });

    // Sync geocoding failed state from hook
    useEffect(() => {
        if (geocodingFailed) {
            setGeocoderFailed(true);
        }
    }, [geocodingFailed]);

    // Sync geocoded bounds and search term when geocoding completes
    useEffect(() => {
        if (!geocodedBounds || !locationToGeocode) {
            return;
        }

        // Only set searchTerm for ?search= query param flows
        if (queryParams.search) {
            setSearchTerm(locationToGeocode);
        }
        setCustomSearchBounds(geocodedBounds);
        currentActiveBoundsRef.current = geocodedBounds;
        fetchCatalogs(geocodedBounds);
    }, [geocodedBounds, locationToGeocode, queryParams.search, fetchCatalogs]);

    // Sync geocoder failed state and search term
    useEffect(() => {
        if (geocoderFailed && locationToGeocode && queryParams.search) {
            setSearchTerm(locationToGeocode);
        }
    }, [geocoderFailed, locationToGeocode, queryParams.search]);

    // Calculate map bounds
    const mapBounds = useMemo(() => {
        if (geoArea?.northEastLatitude && geoArea?.southWestLatitude) {
            return {
                northEast: { lat: geoArea.northEastLatitude, lng: geoArea.northEastLongitude },
                southWest: { lat: geoArea.southWestLatitude, lng: geoArea.southWestLongitude },
            };
        }

        if (customSearchBounds) {
            return customSearchBounds;
        }

        return defaultBounds;
    }, [customSearchBounds, geoArea, defaultBounds]);

    // Handlers
    const handleSearchResults = useCallback(({ northEast, southWest }: MapBounds) => {
        const bounds = { northEast, southWest };
        currentActiveBoundsRef.current = bounds;
        setCustomSearchBounds(bounds);
        hasUserInteractedWithMapRef.current = true;
        setPageNumber(defaultPageNumber);
        // fetchCatalogs will be called by the filter/pagination effect
    }, []);

    const handleBoundsChange = useCallback(
        ({ northEast, southWest }: MapBounds) => {
            // Skip if we're waiting for geocoding to complete
            if (locationToGeocode && !customSearchBounds) {
                return;
            }

            // Skip programmatic bounds changes (initial fitBounds settling)
            if (!hasUserInteractedWithMapRef.current) {
                return;
            }

            const bounds = { northEast, southWest };
            currentActiveBoundsRef.current = bounds;
            // Update ref eagerly so fetchCatalogs reads the reset page, not a stale value
            filtersRef.current = { ...filtersRef.current, pageNumber: defaultPageNumber };
            setPageNumber(defaultPageNumber);
            fetchCatalogs(bounds);
        },
        [fetchCatalogs, locationToGeocode, customSearchBounds]
    );

    const handleUserMapInteraction = useCallback(() => {
        hasUserInteractedWithMapRef.current = true;
    }, []);

    const handleGoogleMapsReady = useCallback(() => {
        setGoogleMapsReady(true);
    }, []);

    // Filter handlers — re-fetch from backend, reset to page 1
    const toggleTopRatedFilter = useCallback(() => {
        setTopRatedFilter((prev) => !prev);
        setPageNumber(defaultPageNumber);
    }, []);

    const toggleLocalPickupFilter = useCallback(() => {
        setLocalPickupFilter((prev) => !prev);
        setPageNumber(defaultPageNumber);
    }, []);

    const handleKeywordsChange = useCallback((newKeywords: string[]) => {
        setKeywords(newKeywords);
        setPageNumber(defaultPageNumber);
    }, []);

    // Pagination handlers
    const handlePageChange = useCallback((page: number) => {
        setPageNumber(page);
        window.scrollTo(0, 0);
    }, []);

    const handlePageSizeChange = useCallback((newPageSize: number) => {
        setPageSize(newPageSize);
        setPageNumber(defaultPageNumber);
    }, []);

    // Build a stable key from route params to detect navigation between different URLs
    const routeKey = useMemo(() => {
        if ('geoAreaId' in routeParams && routeParams.geoAreaId) {
            return `old:${routeParams.geoAreaId}`;
        }
        if ('city' in routeParams || 'state' in routeParams || 'country' in routeParams) {
            const city = 'city' in routeParams ? routeParams.city : '';
            const state = 'state' in routeParams ? routeParams.state : '';
            const country = 'country' in routeParams ? routeParams.country : '';
            return `new:${city}:${state}:${country}`;
        }
        return '';
    }, [routeParams]);

    // Skip all fetch effects on initial mount — the data loader already fetched.
    // The effect that sets this to true is declared AFTER the effects that check it,
    // so they all see `false` on the initial render's effect pass.
    const hasMountedRef = useRef(false);

    // Effect: Keep pageNumber in sync with the URL — covers browser back/forward and a
    // bookmarked/pasted ?page=N link, neither of which calls handlePageChange directly.
    useEffect(() => {
        setPageNumber(urlPageNumber);
    }, [urlPageNumber]);

    // Re-fetch when filters or pagination change
    useEffect(() => {
        if (!hasMountedRef.current) {
            return;
        }
        const activeBounds = getActiveBounds();
        fetchCatalogs(activeBounds);
    }, [topRatedFilter, localPickupFilter, keywords, pageNumber, pageSize, getActiveBounds, fetchCatalogs]);

    // Effect: Clear custom search bounds when navigating to a predefined geo area
    useEffect(() => {
        if (routeKey) {
            setCustomSearchBounds(null);
            setSearchTerm('');
            currentActiveBoundsRef.current = null;
            lastFetchParamsRef.current = null;
            hasUserInteractedWithMapRef.current = false;
        }
    }, [routeKey]);

    // Effect: Reset to defaults when navigating to base page
    useEffect(() => {
        if (hasNewUrlParams || hasOldUrlParams || queryParams.search || geoArea) {
            return;
        }

        if (customSearchBounds) {
            setCustomSearchBounds(null);
            setSearchTerm('');
            currentActiveBoundsRef.current = null;
            lastFetchParamsRef.current = null;
            hasUserInteractedWithMapRef.current = false;
            fetchCatalogs(defaultBounds);
        }
    }, [
        hasNewUrlParams,
        hasOldUrlParams,
        queryParams.search,
        geoArea,
        customSearchBounds,
        defaultBounds,
        fetchCatalogs,
    ]);

    // Effect: Fetch catalogs for predefined geoArea (from RelatedGeoAreas links)
    useEffect(() => {
        if (!hasMountedRef.current) {
            return;
        }

        if (!geoArea || customSearchBounds || queryParams.search) {
            return;
        }

        if (!geoArea.northEastLatitude || !geoArea.southWestLatitude) {
            return;
        }

        const bounds: MapBounds = {
            northEast: { lat: geoArea.northEastLatitude, lng: geoArea.northEastLongitude },
            southWest: { lat: geoArea.southWestLatitude, lng: geoArea.southWestLongitude },
        };

        fetchCatalogs(bounds);
    }, [geoArea, customSearchBounds, queryParams.search, fetchCatalogs]);

    // Mark as mounted AFTER all mount-skipping effects so they see false on the first pass
    useEffect(() => {
        hasMountedRef.current = true;
    }, []);

    return {
        catalogIds,
        catalogsNearMe,
        geoArea,
        geocoderFailed,
        handleBoundsChange,
        handleGoogleMapsReady,
        handleKeywordsChange,
        handlePageChange,
        handlePageSizeChange,
        handleSearchResults,
        handleUserMapInteraction,
        hasFilteredResults,
        isGeocoding,
        loading,
        mapBounds,
        mapPoints,
        pageNumber,
        pageSize,
        searchTerm,
        setGeocoderFailed,
        setSearchTerm,
        toggleLocalPickupFilter,
        toggleTopRatedFilter,
        totalRecords,
        totalUniqueHouses,
    };
};
